import DocumentHeader from '@/components/DocumentHeader';
import DocumentSchema from '@/components/DocumentSchema';
import { SWS_HOST } from '@/constants';
import type { ResolvedQueryData } from '@/utilities/genericTypes';
import type {
  SearchResultsPage,
  Article,
  ListItem,
  ItemList,
} from 'schema-dts';
import type { useNarrativesCommunityInfinite } from '../hooks/useNarrativesCommunityInfinite';
import { narrativeListPageParamsSafeParse } from '../utils';
import { gql } from '@/utilities/gql';
import { useCloudflareImage } from '@/hooks/useCloudflareImage';
import { usePathname } from '@/hooks/location/usePathname';

type Props = {
  narratives: ResolvedQueryData<typeof useNarrativesCommunityInfinite>;
  seoTitle: string;
  seoDescription: string;
  seoImageUrl: string;
};

export const NARRATIVE_COMMUNITY_LIST_DOCUMENT_HEADER_FRAGMENT = gql(`
  fragment NarrativeCommunityListDocumentHeaderFields on Narrative @_unmask {
    slug
    title
    updatedAt
    createdAt
    owner {
      displayName
      role
    }
    company {
      canonicalCommunityUrl
    }
  }
`);

export const NarrativeCommunityListDocumentHeader = ({
  narratives,
  seoTitle,
  seoDescription,
  seoImageUrl,
}: Props) => {
  const pathname = usePathname();
  const canonicalUrl = `${SWS_HOST}${pathname}`;

  const firstPageParams = narrativeListPageParamsSafeParse(
    narratives.pageParams[0]
  );

  const firstPage = narratives.pages[0].narratives;

  const coverImageUrl = useCloudflareImage({
    src: seoImageUrl,
    width: 1200,
    height: 630,
  });
  const targetCanonicalUrl = firstPageParams.after
    ? `${canonicalUrl}?after=${firstPageParams.after}`
    : canonicalUrl;

  const hasNarratives = firstPage.edges.length > 0;

  return (
    <>
      <DocumentHeader
        title={seoTitle}
        allowCrawlers={firstPage.edges.length > 0}
        description={seoDescription}
        openGraphImageUrl={coverImageUrl}
      >
        {hasNarratives && <link rel="canonical" href={targetCanonicalUrl} />}
        {hasNarratives && (
          <meta property="og:url" content={targetCanonicalUrl} />
        )}
      </DocumentHeader>
      {RUNTIME_ENV === 'server' && (
        <DocumentSchema<SearchResultsPage>
          item={{
            '@context': 'https://schema.org',
            '@type': 'SearchResultsPage',
            '@id': canonicalUrl,
            url: canonicalUrl,
            mainEntity: [
              {
                '@type': 'ItemList',
                name: seoTitle,
                itemListOrder: 'https://schema.org/ItemListOrderDescending',
                itemListElement: firstPage.edges.map((narrative, i) => {
                  const { slug, title, updatedAt, createdAt, owner, company } =
                    narrative.node;

                  const { canonicalCommunityUrl } = company ?? {};
                  return {
                    '@type': 'ListItem',
                    position: i + 1,
                    item: {
                      '@type': 'Article',
                      '@id': canonicalCommunityUrl
                        ? `${SWS_HOST}${canonicalCommunityUrl}/${slug}`
                        : undefined,
                      headline: title,
                      dateModified: updatedAt,
                      dateCreated: createdAt,
                      publisher: {
                        '@type': 'Organization',
                        name: 'Simply Wall St',
                        legalName: 'Simply Wall St',
                        url: SWS_HOST,
                        logo: {
                          '@type': 'ImageObject',
                          url: `${SWS_HOST}/news/images/sws-logo.svg`,
                        },
                      },
                      author: {
                        '@type': 'Person',
                        name: owner.displayName,
                        jobTitle: owner.role,
                      },
                    } as Article,
                  } as ListItem;
                }),
              } as ItemList,
            ],
            isPartOf: {
              '@type': 'WebSite',
              url: SWS_HOST,
              name: 'Simply Wall Street',
            },
          }}
        />
      )}
    </>
  );
};
